import { COPYABLE_MARKS } from '../plugins/Formatting/tools/formatCopying';
import { Editor, Text, type NodeEntry } from 'slate';
import type { CustomText } from '../types/aha-editor';

/**
 * Execute the passed in function without running
 * any normalizations.
 *
 * What's the difference between this and Editor.withoutNormalizing?
 * Editor.withoutNormalizing runs the normalizations after the passed
 * in function has finished executing. This function does not run
 * normalizations at all. USE WITH CARE.
 *
 * In this context, it is necessary because normalizations are messing
 * up with our content. When we have an off-screen editor with an
 * inline and we try to apply a mark to it, the normalizations remove
 * the inline.
 *
 * See CREATE-1791-1
 */
export const suppressNormalizations = (editor: Editor, fn: () => void) => {
  Editor.setNormalizing(editor, false);
  try {
    fn();
  } finally {
    Editor.setNormalizing(editor, true);
  }
};

class MarkUtils {
  static add(editor: Editor, mark: string, value: unknown = true) {
    /*
     * Because marks are applied on leaf nodes, which don't
     * have default normalizations in slate, it is safe to suppress
     * normalizations while toggling them. Addresses CREATE-1791-1.
     */
    suppressNormalizations(editor, () => {
      Editor.addMark(editor, mark, value);
    });
  }

  static remove(editor: Editor, mark: string) {
    /*
     * Because marks are applied on leaf nodes, which don't
     * have default normalizations in slate, it is safe to suppress
     * normalizations while toggling them. Addresses CREATE-1791-1.
     */
    suppressNormalizations(editor, () => {
      Editor.removeMark(editor, mark);
    });
  }

  static getFirstTextNodeMarks(editor: Editor) {
    const elements = Array.from(Editor.nodes(editor, { match: Text.isText }));

    if (!elements || elements.length === 0) {
      return {};
    }

    const entry = elements.pop() as NodeEntry<Text>;
    const [node] = entry;
    const { text, ...marks } = node;
    return marks;
  }

  static getFirstTextNodeMark(editor: Editor, mark: string): boolean | string {
    const marks = this.getFirstTextNodeMarks(editor);

    return marks[mark];
  }

  static hasMark(editor: Editor, mark: string): boolean {
    const elements = Array.from(
      Editor.nodes(editor, {
        match: (element) =>
          Text.isText(element) && typeof element[mark] !== 'undefined',
      })
    );

    return elements.length !== 0;
  }

  // Gets the value of mark at the current editor selection.
  // If the mark is not present, returns undefined.
  static getMarkAtSelection(editor: Editor, mark: string) {
    const marks = Editor.marks(editor);

    if (marks?.[mark]) {
      return marks[mark];
    }
  }

  // Captures the current marks, runs the callback, and restores the previous marks
  static withRestoredMarks(editor: Editor, callback: (editor: Editor) => void) {
    const marks = Editor.marks(editor) || {};

    callback(editor);

    Object.entries(marks).forEach(([key, value]) => {
      Editor.addMark(editor, key, value);
    });
  }

  /**
   * This will copy the "copyable" marks from one text node to another.
   */
  static copyMarks({
    source,
    target,
  }: {
    source: CustomText;
    target: CustomText;
  }) {
    COPYABLE_MARKS.forEach((mark) => {
      const value = source[mark];
      if (value != null) {
        // @ts-expect-error The key/value type are dynamic
        target[mark] = value;
      }
    });
  }
}

export default MarkUtils;
